import { Box, HStack, PrimaryButton, Stack } from "flicket-ui";
import { ReactNode } from "react";
import Skeleton from "react-loading-skeleton";

import { useExpressCheckoutContext } from "~features/checkout/express/components/ExpressCheckout.context";
import { ExpressCheckoutButtonRow } from "~features/checkout/express/components/ExpressCheckoutButtonRow";
import { CreateOrderOptions } from "~hooks/reservation/useCreateOrder";
import { useStripeConfigContext } from "~lib/stripeConfigContext";

interface ExpressCheckoutFooterProps {
  priceInfo: ReactNode;
  buttonText: string;
  onAction: (options?: CreateOrderOptions) => void;
  isLoading: boolean;
  isDisabled: boolean;
  isFreeOrder?: boolean;
}

export const ExpressCheckoutFooter = ({
  priceInfo,
  buttonText,
  onAction,
  isLoading,
  isDisabled,
  isFreeOrder,
}: ExpressCheckoutFooterProps) => {
  const expressCheckoutContext = useExpressCheckoutContext();

  const loadingExpressCheckout =
    expressCheckoutContext?.isLoadingExpressCheckoutElement;

  const moreThanOnePaymentMethod =
    expressCheckoutContext?.availablePaymentMethods?.length > 1;
  const breakpoint = moreThanOnePaymentMethod ? "xs" : "xxs";

  const { expressCheckoutEnabled } = useStripeConfigContext();

  if (!expressCheckoutEnabled) {
    return null;
  }

  return (
    <Stack
      width="100%"
      alignItems={{ _: "flex-start", [breakpoint]: "center" }}
      justifyContent={{ _: "flex-start", [breakpoint]: "space-between" }}
      gap={2}
      flexWrap="wrap"
      direction={{ _: "vertical", [breakpoint]: "horizontal" }}
    >
      <Box flexShrink={0}>{priceInfo}</Box>

      <Stack
        minWidth={0}
        direction="horizontal"
        gap={1}
        alignItems="flex-end"
        justifyContent="flex-end"
        flex={1}
        width="100%"
        maxW={{ [breakpoint]: "526px" }}
      >
        {loadingExpressCheckout && !isFreeOrder ? (
          <HStack gap={1}>
            <Skeleton height="48px" width="120px" />
            <Skeleton height="48px" width="120px" />
          </HStack>
        ) : (
          <>
            <PrimaryButton
              flex={1}
              width={{ _: "auto" }}
              maxW={{ [breakpoint]: "160px" }}
              onClick={() => void onAction()}
              disabled={isDisabled}
              isLoading={isLoading}
            >
              {buttonText}
            </PrimaryButton>

            {!isFreeOrder && (
              <ExpressCheckoutButtonRow
                breakpoint={breakpoint}
                disabled={isDisabled}
                onClick={(paymentType) => {
                  onAction({
                    isExpressCheckout: true,
                    paymentType,
                  });
                }}
              />
            )}
          </>
        )}
      </Stack>
    </Stack>
  );
};
